import { useEffect, useState } from 'react'
import {
  LocalAudioTrackPublication,
  LocalVideoTrack,
  LocalVideoTrackPublication,
  Participant,
  RemoteAudioTrack,
  RemoteTrackPublication,
  RemoteVideoTrack,
  TwilioError,
} from 'twilio-video'

import ErrorBoundary from '../../../containers/error_boundary'
import { useCallManager } from '../../../contexts/call_manager_context'
import { CS } from '../call_manager/connection_status_reducer'
import { useIsLocalParticipant } from '../participant_manager/use_is_local_participant'
import { CAMERA_VIDEO_NAME } from '../util/web_rtc_config'

// Get the published video (camera or screen share) track for a participant (local or remote participant)
export const useVideoTrack = (
  participant?: Participant | null,
  name: string = CAMERA_VIDEO_NAME,
) => {
  const [videoTrack, setVideoTrack] = useState<
    LocalVideoTrack | RemoteVideoTrack | null
  >(null)
  const [videoPublication, setVideoPublication] =
    useState<LocalVideoTrackPublication | null>(null)

  const isLocalParticipant = useIsLocalParticipant(participant)
  const {
    activeCall: { connectionStatus, prevConnectionStatus },
  } = useCallManager()

  useEffect(() => {
    return () => {
      setVideoPublication(null)
      setVideoTrack(null)
    }
  }, [participant, name])

  useEffect(() => {
    if (participant) {
      const publication = Array.from(participant.videoTracks).find((entry) =>
        entry?.[1]?.track?.name.startsWith(name),
      )?.[1]
      if (publication) {
        if (isLocalParticipant) {
          setVideoPublication(publication as LocalVideoTrackPublication)
        }
        setVideoTrack(publication.track)
      }
    }
  }, [isLocalParticipant, name, participant])

  // responsible for nullifying track, as track is unpublished, but not stopped
  useEffect(() => {
    if (
      (prevConnectionStatus === CS.ROOM_CONNECTED ||
        prevConnectionStatus === CS.ROOM_RECONNECTING) &&
      connectionStatus === CS.CALL_CONNECTED &&
      videoTrack
    ) {
      setVideoTrack(null)
      videoTrack.detach()
    }
  }, [connectionStatus, participant, prevConnectionStatus, videoTrack])

  // For remote participant
  useEffect(() => {
    if (participant && !isLocalParticipant) {
      const trackSubscribed = (track: RemoteAudioTrack | RemoteVideoTrack) => {
        if (track.kind === 'video' && track.name.startsWith(name)) {
          setVideoTrack(track)
        }
      }

      const trackUnsubscribed = (
        track: RemoteAudioTrack | RemoteVideoTrack,
      ) => {
        if (track.kind === 'video' && track.name.startsWith(name)) {
          setVideoTrack(null)
          track.detach()
        }
      }

      const trackSubscriptionFailed = (
        error: TwilioError,
        publication: RemoteTrackPublication,
      ) => {
        if (
          publication.kind === 'video' &&
          publication.track?.name.startsWith(name)
        ) {
          ErrorBoundary.logErrorToMyService(error, publication)
        }
      }

      participant.on('trackSubscribed', trackSubscribed)
      participant.on('trackUnsubscribed', trackUnsubscribed)
      participant.on('trackSubscriptionFailed', trackSubscriptionFailed)

      return () => {
        participant.off('trackSubscribed', trackSubscribed)
        participant.off('trackUnsubscribed', trackUnsubscribed)
        participant.off('trackSubscriptionFailed', trackSubscriptionFailed)
      }
    }
  }, [participant, isLocalParticipant, name])

  // For local participant
  useEffect(() => {
    if (participant && isLocalParticipant) {
      const trackPublished = (
        publication: LocalAudioTrackPublication | LocalVideoTrackPublication,
      ) => {
        if (
          publication.kind === 'video' &&
          publication.track?.name.startsWith(name)
        ) {
          setVideoPublication(publication)
          setVideoTrack(publication.track)
        }
      }

      participant.on('trackPublished', trackPublished)

      return () => {
        participant.off('trackPublished', trackPublished)
      }
    }
  }, [participant, isLocalParticipant, name])

  // There is no trackUnpublished event for local participant, this is an alternative way
  useEffect(() => {
    // using videoPublication to determine if it is a local participant,
    // as isLocalParticipant is set to undefined, listener is removed, but not reattached causing the cleanup to fail
    if (videoPublication && videoTrack) {
      const onStopped = (track: LocalVideoTrack) => {
        videoPublication?.unpublish()
        setVideoPublication(null)
        setVideoTrack(null)
        track.detach()
      }
      videoTrack.on('stopped', onStopped)

      return () => {
        videoTrack.off('stopped', onStopped)
      }
    }
  }, [videoPublication, videoTrack])

  return videoTrack
}
